// src/hooks/useSegmentedRecorder.js
// Sarvam's speech-to-text endpoint rejects any single audio clip longer than
// 30 seconds. Rather than caching that limit into every caller, this hook
// keeps recording for as long as the user wants, but transparently rolls the
// underlying MediaRecorder over onto a fresh instance every SEGMENT_SECONDS —
// each segment is its own valid, independently-decodable webm file, well
// under the limit. Callers get back an array of Blobs to transcribe one at a
// time and concatenate, instead of a single (possibly too-long) blob.
import { useCallback, useRef, useState } from 'react';

const SEGMENT_SECONDS = 25; // safely under Sarvam's 30s cap

export function useSegmentedRecorder() {
  const [recording, setRecording] = useState(false);
  const [timeElapsed, setTime]     = useState(0);
  const [segments, setSegments]    = useState<Blob[]>([]);
  const [error, setError]          = useState('');

  const mediaRef          = useRef<MediaRecorder | null>(null);
  const streamRef          = useRef<MediaStream | null>(null);
  const isRecordingRef     = useRef(false);
  const segmentsRef        = useRef<Blob[]>([]);
  const segmentTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uiTimerRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopResolveRef     = useRef<((segments: Blob[]) => void) | null>(null);

  const beginSegment = useCallback((stream: MediaStream) => {
    const mimeType = (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
      ? 'audio/webm;codecs=opus' : '';
    const mr = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 128000 } : {});
    mediaRef.current = mr;
    const chunks: Blob[] = [];
    mr.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
      if (blob.size > 0) {
        segmentsRef.current = [...segmentsRef.current, blob];
        setSegments(segmentsRef.current);
      }
      if (isRecordingRef.current) {
        beginSegment(stream); // still recording overall — roll seamlessly onto a new segment
      } else {
        stream.getTracks().forEach(t => t.stop());
        if (stopResolveRef.current) { stopResolveRef.current(segmentsRef.current); stopResolveRef.current = null; }
      }
    };
    mr.start(); // no timeslice — each segment buffers internally, flushed whole on stop()
    segmentTimeoutRef.current = setTimeout(() => {
      if (mediaRef.current === mr && mr.state === 'recording') mr.stop();
    }, SEGMENT_SECONDS * 1000);
  }, []);

  const start = useCallback(async () => {
    segmentsRef.current = [];
    setSegments([]); setError(''); setTime(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      isRecordingRef.current = true;
      beginSegment(stream);
      setRecording(true);
      uiTimerRef.current = setInterval(() => setTime(t => t + 1), 1000);
    } catch (e) {
      setError('Microphone access denied. Please allow microphone access and try again.');
    }
  }, [beginSegment]);

  // Resolves once the final segment has actually been captured — use this
  // when you need to act on the complete segment list right after stopping
  // (e.g. auto-transcribe), since the last segment lands asynchronously.
  const stopAndWait = useCallback(() => {
    isRecordingRef.current = false;
    if (segmentTimeoutRef.current) clearTimeout(segmentTimeoutRef.current);
    if (uiTimerRef.current) clearInterval(uiTimerRef.current);
    setRecording(false);
    return new Promise<Blob[]>(resolve => {
      if (mediaRef.current?.state === 'recording') {
        stopResolveRef.current = resolve;
        mediaRef.current.stop();
      } else {
        resolve(segmentsRef.current);
      }
    });
  }, []);

  const stop = useCallback(() => { stopAndWait(); }, [stopAndWait]);

  const reset = useCallback(() => {
    segmentsRef.current = [];
    setSegments([]); setError(''); setTime(0);
  }, []);

  const totalBytes = segments.reduce((s, b) => s + b.size, 0);

  // Exposes the live MediaStream so a caller can attach a Web Audio API
  // analyser for voice-activity detection (see utils/voiceActivityDetector.js)
  // without requesting a second, independent getUserMedia stream.
  const getStream = useCallback(() => streamRef.current, []);

  return { recording, timeElapsed, segments, totalBytes, error, start, stop, stopAndWait, reset, getStream };
}
